"use client";

import { c as _c } from "react/compiler-runtime";
import type { CurrentRefinementsConnectorParamsItem } from "instantsearch.js/es/connectors/current-refinements/connectCurrentRefinements";
import { useEffect, useRef } from "react";
import { useCurrentRefinements, useHits, useInstantSearch } from "react-instantsearch";
import { type EcommerceProduct, type ProductListFilterEntry, toEcommerceProduct, trackProductListFiltered, trackProductListViewed } from "@/lib/analytics/segment";
import type { PlpFilterGroupConfig } from "@/lib/contentful/filterGroups";
import type { TypesenseHit } from "@/lib/search/types";
import { toProductHit } from "@/lib/search/typesenseInstantsearchHit";
import { useFilterGroups } from "./PlpFilterGroupsContext";
const PLP_LIST_ID = "product_list";
function buildAppliedFilters(items: CurrentRefinementsConnectorParamsItem[], groups: PlpFilterGroupConfig[]): ProductListFilterEntry[] {
  const filters: ProductListFilterEntry[] = [];
  for (const item of items) {
    const group = groups.find(candidate => candidate.slug === item.attribute);
    if (!group) continue;
    for (const refinement of item.refinements) {
      const key = String(refinement.value);
      const label = group.values.find(value => value.productAttributeKey === key)?.label ?? key;
      filters.push({
        type: group.label,
        value: label
      });
    }
  }
  return filters;
}
function snapshotRefinements(items: CurrentRefinementsConnectorParamsItem[]): Map<string, Set<string>> {
  const snapshot = new Map<string, Set<string>>();
  for (const item of items) {
    const set = new Set<string>();
    for (const refinement of item.refinements) set.add(String(refinement.value));
    snapshot.set(item.attribute, set);
  }
  return snapshot;
}
function setsEqual(left: Set<string>, right: Set<string>): boolean {
  if (left.size !== right.size) return false;
  for (const key of left) if (!right.has(key)) return false;
  return true;
}
function refinementsChanged(prev: Map<string, Set<string>> | null, next: Map<string, Set<string>>): boolean {
  if (prev === null) return false;
  const empty = new Set<string>();
  for (const attribute of new Set([...prev.keys(), ...next.keys()])) {
    if (!setsEqual(prev.get(attribute) ?? empty, next.get(attribute) ?? empty)) return true;
  }
  return false;
}
function buildProducts(hits: TypesenseHit[]): EcommerceProduct[] {
  // hitsPerPage is 48 with no pagination widget, so the hit index is the
  // absolute 1-based position. Skip hits toProductHit rejects (missing
  // sku/slug/name) without disturbing the positions of the rest.
  const products: EcommerceProduct[] = [];
  hits.forEach((hit, index) => {
    const productHit = toProductHit(hit);
    if (productHit) products.push(toEcommerceProduct(productHit, index + 1));
  });
  return products;
}
interface Props {
  category?: string;
}
export function PlpAnalyticsBridge(t0) {
  const $ = _c(8);
  const {
    category
  } = t0;
  const groups = useFilterGroups();
  const {
    status,
    results
  } = useInstantSearch();
  const {
    items
  } = useCurrentRefinements();
  const {
    items: hits
  } = useHits();
  const previousQuery = useRef(null);
  const previousRefinements = useRef(null);
  let t1;
  let t2;
  if ($[0] !== category || $[1] !== groups || $[2] !== hits || $[3] !== items || $[4] !== results || $[5] !== status) {
    t1 = () => {
      if (status !== "idle" || !results) {
        return;
      }
      const query = results.query ?? "";
      const nextRefinements = snapshotRefinements(items);
      const isFirstSettle = previousQuery.current === null;
      const queryChanged = !isFirstSettle && previousQuery.current !== query;
      const filtersChanged = refinementsChanged(previousRefinements.current, nextRefinements);
      previousQuery.current = query;
      previousRefinements.current = nextRefinements;
      const products = buildProducts(hits);
      if (isFirstSettle || queryChanged) {
        trackProductListViewed({
          listId: PLP_LIST_ID,
          category,
          products
        });
        return;
      }
      if (filtersChanged) {
        const filters = buildAppliedFilters(items, groups);
        trackProductListFiltered({
          listId: PLP_LIST_ID,
          category,
          filters,
          products
        });
      }
    };
    t2 = [status, results, items, hits, groups, category];
    $[0] = category;
    $[1] = groups;
    $[2] = hits;
    $[3] = items;
    $[4] = results;
    $[5] = status;
    $[6] = t1;
    $[7] = t2;
  } else {
    t1 = $[6];
    t2 = $[7];
  }
  useEffect(t1, t2);
  return null;
}